import React, { useState, useMemo, useEffect } from "react";
import {
  Button,
  Header,
  Modal,
  Form,
  TextArea,
  Input,
  Grid,
  Segment,
  Checkbox,
  Message,
} from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "../../../app/common/modals/modalSlice";
import { addEmailToDb, fetchComplianceItemsFromDb, sendComplianceNotificationEmail } from "../../../app/firestore/firestoreService";
import { convertAddressFull } from "../../../app/common/util/util";
import { serverTimestamp, onSnapshot } from "firebase/firestore";
import { toast } from "react-toastify";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import { useMediaQuery } from "react-responsive";
import { format } from "date-fns";

export default function ComplianceNotificationModal({ transaction, template, documents, brokerageForms = [] }) {
  const dispatch = useDispatch();
  const { currentUserProfile } = useSelector((state) => state.profile);
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });

  const [selectedRecipients, setSelectedRecipients] = useState({
    agent: false,
    coAgent: false,
    tc: false,
    assistant: false,
    manager: false,
    managerAssistant: false,
  });

  const [customMessage, setCustomMessage] = useState(
    `There are missing compliance requirements for the transaction at ${
      transaction?.address ? convertAddressFull(transaction.address) : transaction?.title
    }. Please review the Compliance checklist and ensure all required documents are completed.`
  );

  const [customSubject, setCustomSubject] = useState(
    `Compliance Requirements - ${
      transaction?.address ? convertAddressFull(transaction.address) : transaction?.title
    }`
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedComplianceItems, setSavedComplianceItems] = useState({});

  const transactionId = transaction?.id;

  // Fetch saved compliance items from database to reflect any status changes
  useEffect(() => {
    if (!transactionId) return;

    const unsubscribe = onSnapshot(
      fetchComplianceItemsFromDb(transactionId),
      (snapshot) => {
        const items = {};
        snapshot.docs.forEach((doc) => {
          items[doc.id] = doc.data();
        });
        setSavedComplianceItems(items);
      },
      (error) => {
        console.error("Error fetching saved compliance items:", error);
      }
    );

    return () => unsubscribe();
  }, [transactionId]);

  // Build compliance items from template - reflects saved status changes (not-needed, optional, etc.)
  const complianceItems = useMemo(() => {
    if (!template?.categories || !documents) return [];

    const items = [];
    const propertyType = transaction?.propertyType || "Residential";

    template.categories.forEach((category) => {
      (category.items || []).forEach((item) => {
        // Check if there's a saved status for this item
        const itemKey = `${category.name}-${item.name}`;
        const savedItem = savedComplianceItems[itemKey];

        // Get the effective requirement (saved requirement overrides template)
        const effectiveRequirement = savedItem?.requirement || item.requirement;

        // Skip items marked as "not-needed" or changed to optional
        if (savedItem?.status === "not-needed") return;
        if (effectiveRequirement !== "required") return;

        // Check if item applies to this transaction's property type
        const appliesToPropertyType =
          !item.appliesToPropertyTypes ||
          item.appliesToPropertyTypes.length === 0 ||
          item.appliesToPropertyTypes.includes(propertyType) ||
          item.appliesToPropertyTypes.includes("All");

        if (appliesToPropertyType) {
          // Find matching document by documentName
          // The template maps documentName to documentType and documentTypeSubtype
          const matchingDoc = documents.find((doc) => {
            if (!doc.signingComplete) return false;

            // Match by documentName only
            if (item.documentName && doc.documentName === item.documentName) {
              return true;
            }

            return false;
          });

          items.push({
            ...item,
            category: category.name,
            categoryOrder: category.order,
            document: matchingDoc,
            status: matchingDoc ? "complete" : (item.status || "pending"),
            requirement: effectiveRequirement,
          });
        }
      });
    });

    // Sort by category order, then item order
    return items.sort((a, b) => {
      if (a.categoryOrder !== b.categoryOrder) {
        return a.categoryOrder - b.categoryOrder;
      }
      return (a.order || 0) - (b.order || 0);
    });
  }, [template, transaction, documents, savedComplianceItems]);

  // Filter uploaded brokerage form documents
  const uploadedBrokerageFormDocs = useMemo(() => {
    if (!documents || documents.length === 0) return [];
    return documents.filter((doc) => doc.isBrokerageForm === true && doc.signingComplete === true);
  }, [documents]);

  // Build required brokerage form items with their matched documents
  const brokerageFormItems = useMemo(() => {
    if (!brokerageForms || brokerageForms.length === 0) return [];

    const agentRepresents = transaction?.agentRepresents || "Buyer";

    return brokerageForms
      .filter((form) => {
        // Only include required brokerage forms
        if (form.complianceRequirement !== "required") return false;

        // Filter based on agent representation
        if (agentRepresents === "Buyer" || agentRepresents === "Tenant") {
          if (form.showBuyerAgent === false) return false;
        } else if (agentRepresents === "Seller" || agentRepresents === "Landlord") {
          if (form.showSellerAgent === false) return false;
        }

        return true;
      })
      .map((form) => {
        // Find ALL matching uploaded documents
        const matchedDocs = uploadedBrokerageFormDocs.filter(
          (doc) => doc.title === form.title || doc.formId === form.id
        );

        return {
          ...form,
          documents: matchedDocs,
          isComplete: matchedDocs.length > 0,
          requirement: "required",
        };
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [brokerageForms, uploadedBrokerageFormDocs, transaction]);

  // Build recipient list
  const availableRecipients = {
    agent: {
      label: "Agent",
      email: transaction?.agentProfile?.email,
      name: `${transaction?.agentProfile?.firstName || ""} ${
        transaction?.agentProfile?.lastName || ""
      }`.trim(),
    },
    coAgent: {
      label: "Co-Agent",
      email: transaction?.coAgent?.email,
      name: `${transaction?.coAgent?.firstName || ""} ${
        transaction?.coAgent?.lastName || ""
      }`.trim(),
      available: !!transaction?.coAgentId,
    },
    tc: {
      label: "TC (Mine)",
      email: transaction?.transactionCoordinator?.email,
      name: `${transaction?.transactionCoordinator?.firstName || ""} ${
        transaction?.transactionCoordinator?.lastName || ""
      }`.trim(),
      available: !!transaction?.tcId,
    },
    assistant: {
      label: "Assistant",
      email: transaction?.assistant?.email,
      name: `${transaction?.assistant?.firstName || ""} ${
        transaction?.assistant?.lastName || ""
      }`.trim(),
      available: !!transaction?.hasAssistant,
    },
    manager: {
      label: "Manager",
      email: transaction?.managerDetails?.managerEmail,
      name: `${transaction?.managerDetails?.firstName || ""} ${
        transaction?.managerDetails?.lastName || ""
      }`.trim(),
      available: !!transaction?.managerId,
    },
    managerAssistant: {
      label: "Manager Assistant",
      email: transaction?.managerAssistantProfile?.email,
      name: `${transaction?.managerAssistantProfile?.firstName || ""} ${
        transaction?.managerAssistantProfile?.lastName || ""
      }`.trim(),
      available: !!transaction?.managerAssistantId,
    },
  };

  function handleRecipientToggle(recipientKey) {
    setSelectedRecipients((prev) => ({
      ...prev,
      [recipientKey]: !prev[recipientKey],
    }));
  }

  function generateComplianceTableHTML() {
    if (!complianceItems || complianceItems.length === 0) {
      return "";
    }

    // Group items by category
    const itemsByCategory = {};
    complianceItems.forEach((item) => {
      if (!itemsByCategory[item.category]) {
        itemsByCategory[item.category] = [];
      }
      itemsByCategory[item.category].push(item);
    });

    // Generate HTML table
    let tableHTML = `
      <table style="width: 100%; border-collapse: collapse; margin: 20px 0; font-family: Arial, sans-serif;">
        <thead>
          <tr style="background-color: #2185d0; color: white;">
            <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Document</th>
            <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Requirement</th>
            <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Status</th>
            <th style="padding: 10px; text-align: left; border: 1px solid #ddd;">Updated</th>
          </tr>
        </thead>
        <tbody>
    `;

    Object.entries(itemsByCategory).forEach(([categoryName, items]) => {
      // Add category header row
      tableHTML += `
        <tr style="background-color: #f8f9fa;">
          <td colspan="4" style="padding: 8px; font-weight: bold; border: 1px solid #ddd;">${categoryName}</td>
        </tr>
      `;

      // Add items for this category
      items.forEach((item) => {
        const requirement = item.status === "not-needed" ? "not-needed" : item.requirement;
        const isComplete = item.status === "complete" || !!item.document;

        // Determine requirement badge color
        let requirementColor = "#6c757d"; // grey for optional/not-needed
        if (requirement === "required") {
          requirementColor = isComplete ? "#21ba45" : "#db2828"; // green or red
        } else if (requirement === "conditional") {
          requirementColor = isComplete ? "#21ba45" : "#f2711c"; // green or orange
        }

        // Determine status
        let statusText = "Pending";
        let statusColor = "#f2711c"; // orange
        if (item.document) {
          statusText = "Complete";
          statusColor = "#21ba45"; // green
        } else if (item.status === "not-needed") {
          statusText = "Not Needed";
          statusColor = "#6c757d"; // grey
        }

        // Format updated date - handle Firestore timestamps, Date objects, or date strings
        let updatedDate = "—";
        const dateField = item.document?.updatedAt || item.document?.createdAt;
        if (dateField) {
          try {
            let dateObj;
            if (dateField.seconds) {
              // Firestore timestamp
              dateObj = new Date(dateField.seconds * 1000);
            } else if (dateField.toDate) {
              // Firestore Timestamp object with toDate method
              dateObj = dateField.toDate();
            } else {
              // Regular Date or date string
              dateObj = new Date(dateField);
            }
            updatedDate = format(dateObj, "MM/dd/yyyy");
          } catch (e) {
            updatedDate = "—";
          }
        }

        tableHTML += `
          <tr style="background-color: ${isComplete ? "#f0fff4" : "white"};">
            <td style="padding: 8px; border: 1px solid #ddd;">${item.name}</td>
            <td style="padding: 8px; border: 1px solid #ddd;">
              <span style="background-color: ${requirementColor}; color: white; padding: 4px 8px; border-radius: 3px; font-size: 11px; display: inline-block;">
                ${requirement || "optional"}
              </span>
            </td>
            <td style="padding: 8px; border: 1px solid #ddd;">
              <span style="background-color: ${statusColor}; color: white; padding: 4px 8px; border-radius: 3px; font-size: 11px; display: inline-block;">
                ${statusText}
              </span>
            </td>
            <td style="padding: 8px; border: 1px solid #ddd;">${updatedDate}</td>
          </tr>
        `;
      });
    });

    // Add Brokerage Forms section if there are any required brokerage forms
    if (brokerageFormItems && brokerageFormItems.length > 0) {
      // Add Brokerage Forms category header
      tableHTML += `
        <tr style="background-color: #f8f9fa;">
          <td colspan="4" style="padding: 8px; font-weight: bold; border: 1px solid #ddd;">Brokerage Forms</td>
        </tr>
      `;

      brokerageFormItems.forEach((item) => {
        const isComplete = item.isComplete;

        // Required items - green if complete, red if not
        const requirementColor = isComplete ? "#21ba45" : "#db2828";

        // Determine status
        let statusText = "Pending";
        let statusColor = "#f2711c"; // orange
        if (isComplete) {
          statusText = "Complete";
          statusColor = "#21ba45"; // green
        }

        // Format updated date from most recent document
        let updatedDate = "—";
        if (item.documents && item.documents.length > 0) {
          const mostRecentDoc = item.documents[0];
          const dateField = mostRecentDoc?.updatedAt || mostRecentDoc?.createdAt;
          if (dateField) {
            try {
              let dateObj;
              if (dateField.seconds) {
                dateObj = new Date(dateField.seconds * 1000);
              } else if (dateField.toDate) {
                dateObj = dateField.toDate();
              } else {
                dateObj = new Date(dateField);
              }
              updatedDate = format(dateObj, "MM/dd/yyyy");
            } catch (e) {
              updatedDate = "—";
            }
          }
        }

        tableHTML += `
          <tr style="background-color: ${isComplete ? "#f0fff4" : "white"};">
            <td style="padding: 8px; border: 1px solid #ddd;">${item.title}</td>
            <td style="padding: 8px; border: 1px solid #ddd;">
              <span style="background-color: ${requirementColor}; color: white; padding: 4px 8px; border-radius: 3px; font-size: 11px; display: inline-block;">
                required
              </span>
            </td>
            <td style="padding: 8px; border: 1px solid #ddd;">
              <span style="background-color: ${statusColor}; color: white; padding: 4px 8px; border-radius: 3px; font-size: 11px; display: inline-block;">
                ${statusText}
              </span>
            </td>
            <td style="padding: 8px; border: 1px solid #ddd;">${updatedDate}</td>
          </tr>
        `;
      });
    }

    tableHTML += `
        </tbody>
      </table>
    `;

    return tableHTML;
  }

  async function handleSend() {
    // Validate at least one recipient is selected
    const hasRecipient = Object.values(selectedRecipients).some((v) => v);
    if (!hasRecipient) {
      toast.error("Please select at least one recipient");
      return;
    }

    setIsSubmitting(true);

    try {
      // Build recipient email list and roles
      const toEmails = [];
      const toEmailsAndRoles = [];
      Object.entries(selectedRecipients).forEach(([key, isSelected]) => {
        if (isSelected && availableRecipients[key]?.email) {
          const recipient = availableRecipients[key];
          toEmails.push(recipient.email);
          toEmailsAndRoles.push({
            role: recipient.label,
            email: recipient.email,
            firstName: recipient.name?.split(" ")[0] || "",
            lastName: recipient.name?.split(" ").slice(1).join(" ") || "",
          });
        }
      });

      // Prepare email data
      const emailHistoryData = {
        transactionId: transaction.id,
        transactionTitle: transaction.title,
        from: currentUserProfile.email,
        fromName: `${currentUserProfile.firstName} ${currentUserProfile.lastName}`,
        to: toEmails,
        toEmails: toEmails,
        toEmailsAndRoles: toEmailsAndRoles,
        subject: customSubject,
        message: customMessage,
        type: "compliance-notification",
        sentAt: serverTimestamp(),
      };

      // Generate compliance table HTML
      const complianceTableHTML = generateComplianceTableHTML();

      // Create mail options for Firebase mail collection trigger
      const mailOptions = {
        to: toEmails,
        from: `${currentUserProfile.firstName} ${currentUserProfile.lastName} <help@transactioner.com>`,
        replyTo: currentUserProfile.email,
        message: {
          subject: customSubject,
          text: customMessage,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto;">
              <p style="white-space: pre-line;">${customMessage.replace(/\n/g, "<br/>")}</p>
              ${complianceTableHTML}
              <hr/>
              <p style="color: #666; font-size: 12px;">
                Sent from Transactioner by ${currentUserProfile.firstName} ${
            currentUserProfile.lastName
          }
              </p>
            </div>
          `,
        },
      };

      // Add to mail collection to trigger Firebase Email Extension
      await sendComplianceNotificationEmail(mailOptions);

      // Add to email history for record keeping (include message object for "Show HTML" button)
      await addEmailToDb(emailHistoryData, { message: mailOptions.message });

      toast.success("Compliance notification sent successfully!");
      dispatch(closeModal({ modalType: "ComplianceNotificationModal" }));
    } catch (error) {
      console.error("Error sending compliance notification:", error);
      toast.error("Failed to send notification. Please try again.");
      setIsSubmitting(false);
    }
  }

  function handleCancel() {
    dispatch(closeModal({ modalType: "ComplianceNotificationModal" }));
  }

  return (
    <ModalWrapper open={true} size="large">
      <Segment clearing>
        <div
          className={
            isMobile
              ? "zero horizontal margin small top margin"
              : "medium horizontal margin small top margin"
          }
        >
          <Header icon="mail" content="Send Compliance Notification" />
          <Modal.Content>
            <Message info>
              <Message.Header>Notify Team Members</Message.Header>
              <p>
                Send a notification about missing compliance requirements to selected
                team members.
              </p>
            </Message>

            <Form>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <label>
                      <strong>Select Recipients:</strong>
                    </label>
                    <div className="small top margin">
                      {Object.entries(availableRecipients).map(([key, recipient]) => {
                        if (recipient.available === false) return null;
                        if (!recipient.email) return null;

                        return (
                          <div key={key} className="tiny bottom margin">
                            <Checkbox
                              label={`${recipient.label} - ${recipient.name} (${recipient.email})`}
                              checked={selectedRecipients[key]}
                              onChange={() => handleRecipientToggle(key)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <label>
                      <strong>Subject Line:</strong>
                    </label>
                    <Input
                      fluid
                      value={customSubject}
                      onChange={(e) => setCustomSubject(e.target.value)}
                      placeholder="Email subject"
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <label>
                      <strong>Message:</strong>
                    </label>
                    <TextArea
                      rows={4}
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      placeholder="Enter your message here..."
                      style={{ width: "100%" }}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <label>
                      <strong>Compliance Table Preview:</strong>
                    </label>
                    <div
                      style={{
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        padding: "10px",
                        marginTop: "8px",
                        maxHeight: "300px",
                        overflowY: "auto",
                        backgroundColor: "#fafafa"
                      }}
                      dangerouslySetInnerHTML={{ __html: generateComplianceTableHTML() }}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <div className="float-right">
                      <Button onClick={handleCancel} disabled={isSubmitting}>
                        Cancel
                      </Button>
                      <Button
                        primary
                        onClick={handleSend}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      >
                        Send
                      </Button>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          </Modal.Content>
        </div>
      </Segment>
    </ModalWrapper>
  );
}

